import { Fragment, useMemo } from "react";

import { KpiCard } from "src/components/Analytics/KpiCard/KpiCard";
import { DashboardInsightsBand } from "src/components/Dashboard/DashboardInsightsBand";
import { dateHeader } from "src/components/dataView/DataTable/tableHelpers";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import {
  dashboardCohortAtom,
  dashboardUnitEconomicsAtom,
  useAnalytics,
} from "src/lib/hooks/useAnalytics";
import {
  COHORT_ANALYTICS_QUERY,
  UNIT_ECONOMICS_QUERY,
} from "src/lib/queries/analytics";
import { cn } from "src/lib/utils";

import { getProfitabilityCards } from "./profitabilityCards";

const threeKpiCardClassName = "h-full @xl:grid-cols-3";
const fourKpiCardClassName = "h-full @md:grid-cols-2 @5xl:grid-cols-4";

/**
 * Helper to get summary and comparison summary from unit economics data
 * @param {object} unitEconomics - The unit economics data object
 * @param {boolean|null} returningCustomer - Whether to get data for returning customers (true), new customers (false), or all customers (null)
 * @returns {object} An object containing the summary and comparison summary
 */
const getUnitEconomicsSummaries = (
  unitEconomics,
  returningCustomer = false
) => {
  const summary = unitEconomics?.data?.find(
    (item) =>
      item.period === "Summary" && item.returningCustomer === returningCustomer
  );
  const comparison = unitEconomics?.comparisonData?.find(
    (item) =>
      item.period === "Summary" && item.returningCustomer === returningCustomer
  );

  return { summary, comparison };
};

/**
 * Helper to get trend data for a specific field from unit economics data
 * @param {object} unitEconomics - The unit economics data object
 * @param {string} field - The field to get trend data for
 * @param {string} resolution - The resolution of the data (e.g., "day", "week", "month")
 * @param {boolean|null} returningCustomer - Whether to get data for returning customers (true), new customers (false), or all customers (null)
 * @returns {Array} An array of trend data points
 */
const getUnitEconomicsTrend = (
  unitEconomics,
  field,
  resolution,
  returningCustomer = false
) => {
  return (unitEconomics?.data || [])
    .filter(
      (item) =>
        item.period !== "Summary" &&
        item.returningCustomer === returningCustomer
    )
    .sort((a, b) => a.period.localeCompare(b.period))
    .map((item) => ({
      period: dateHeader(item.period, resolution),
      value: item[field] || 0,
    }));
};

const getUnitEconomicsSimpleKpi =
  (field, { noTrend = false, returningCustomer = false } = {}) =>
  (unitEconomics, _, resolution) => {
    const { summary, comparison } = getUnitEconomicsSummaries(
      unitEconomics,
      returningCustomer
    );

    return {
      value: summary?.[field] || 0,
      secondaryValue: comparison?.[field] || 0,
      trend: noTrend
        ? null
        : getUnitEconomicsTrend(
            unitEconomics,
            field,
            resolution,
            returningCustomer
          ),
    };
  };

const getCohortAverageKpi = (field) => (_, cohorts) => {
  const { data, comparisonData } = cohorts || {};

  const avg =
    data && data.length > 0
      ? data.reduce((sum, item) => sum + (item[field] || 0), 0) / data.length
      : 0;
  const comparisonAvg =
    comparisonData && comparisonData.length > 0
      ? comparisonData.reduce((sum, item) => sum + (item[field] || 0), 0) /
        comparisonData.length
      : 0;

  return {
    value: avg,
    secondaryValue: comparisonAvg,
  };
};

const getKpiCards = (numberFormat) => {
  const kpiCards = [
    {
      className: fourKpiCardClassName,
      title: "Profitability Metrics",
      cards: getProfitabilityCards(numberFormat),
    },
    {
      className: threeKpiCardClassName,
      title: "Acquisition Metrics",
      cards: {
        newAov: {
          title: "New AOV",
          format: numberFormat.currency,
          accessor: getUnitEconomicsSimpleKpi("aov"),
        },
        aMER: {
          title: "aMER (0-Day LTR:nCAC)",
          format: numberFormat.whole,
          accessor: (unitEconomics, _, resolution) => {
            const { summary, comparison } = getUnitEconomicsSummaries(
              unitEconomics,
              false
            );
            const aov = summary?.aov || 0;
            const cac = summary?.directMarketing || 1;

            const prevAov = comparison?.aov || 0;
            const prevCac = comparison?.directMarketing || 1;

            const aovTrend = getUnitEconomicsTrend(
              unitEconomics,
              "aov",
              resolution,
              false
            );
            const cacTrend = getUnitEconomicsTrend(
              unitEconomics,
              "directMarketing",
              resolution,
              false
            );

            const trend = aovTrend.map((item, index) => ({
              period: item.period,
              value: cacTrend[index]?.value
                ? item.value / cacTrend[index].value
                : 0,
            }));

            return {
              value: aov / cac,
              secondaryValue: prevAov / prevCac,
              trend,
            };
          },
        },
        zeroDayLTCMnCAC: {
          title: "0-Day LTCM:nCAC",
          format: numberFormat.whole,
          accessor: (unitEconomics, _, resolution) => {
            const { value: amer, secondaryValue: comparisonAmer } = kpiCards
              .find((section) => section.cards.aMER)
              ?.cards.aMER.accessor(unitEconomics) || {
              value: 0,
              secondaryValue: 0,
            };

            const { summary, comparison } = getUnitEconomicsSummaries(
              unitEconomics,
              false
            );
            const grossProfit = summary?.grossProfit || 0;
            const aov = summary?.aov || 0;
            const comparisonGrossProfit = comparison?.grossProfit || 0;
            const comparisonAov = comparison?.aov || 0;

            const aovTrend = getUnitEconomicsTrend(
              unitEconomics,
              "aov",
              resolution,
              false
            );
            const grossProfitTrend = getUnitEconomicsTrend(
              unitEconomics,
              "grossProfit",
              resolution,
              false
            );

            const trend = grossProfitTrend.map((item, index) => ({
              period: item.period,
              value: aovTrend[index]?.value
                ? amer * (item.value / aovTrend[index].value)
                : 0,
            }));

            return {
              value: aov ? amer * (grossProfit / aov) : 0,
              secondaryValue: comparisonAov
                ? comparisonAmer * (comparisonGrossProfit / comparisonAov)
                : 0,
              trend,
            };
          },
        },
      },
    },
    {
      className: fourKpiCardClassName,
      cards: {
        newCustomers: {
          title: "New Customers",
          format: numberFormat.whole,
          accessor: getUnitEconomicsSimpleKpi("orders", {
            noTrend: true,
          }),
        },
        newRefundRate: {
          title: "New Refund Rate",
          format: numberFormat.percentTimes100,
          smallerIsGood: true,
          accessor: getUnitEconomicsSimpleKpi("rr", {
            noTrend: true,
          }),
        },
        newDiscountRate: {
          title: "New Discount Rate",
          format: numberFormat.percentTimes100,
          smallerIsGood: true,
          accessor: getUnitEconomicsSimpleKpi("dr", {
            noTrend: true,
          }),
        },
        nCAC: {
          title: "nCAC",
          format: numberFormat.currency,
          smallerIsGood: true,
          accessor: getUnitEconomicsSimpleKpi("directMarketing", {
            noTrend: true,
          }),
        },
      },
    },
    {
      className: threeKpiCardClassName,
      title: "Retention Metrics",
      cards: {
        repeatAov: {
          title: "Repeat AOV",
          format: numberFormat.currency,
          accessor: getUnitEconomicsSimpleKpi("aov", {
            returningCustomer: true,
          }),
        },
        repeatDiscountRate: {
          title: "Repeat Discount Rate",
          format: numberFormat.percentTimes100,
          smallerIsGood: true,
          accessor: getUnitEconomicsSimpleKpi("dr", {
            returningCustomer: true,
          }),
        },
        repeatRefundRate: {
          title: "Repeat Refund Rate",
          format: numberFormat.percentTimes100,
          smallerIsGood: true,
          accessor: getUnitEconomicsSimpleKpi("rr", {
            returningCustomer: true,
          }),
        },
      },
    },
    {
      className: fourKpiCardClassName,
      cards: {
        repeatCustomers: {
          title: "Repeat Customers",
          format: numberFormat.whole,
          accessor: getUnitEconomicsSimpleKpi("orders", {
            noTrend: true,
            returningCustomer: true,
          }),
        },
        thirtyDayRepeatRate: {
          title: "30-Day Repeat Rate",
          format: numberFormat.percent,
          accessor: getCohortAverageKpi("repeatCustomerRate"),
        },
        ninetyDayLTR: {
          title: "90-Day LTR",
          format: (value) =>
            isNaN(value) ? value : numberFormat.currency(value),
          accessor: (_, cohorts) => {
            const { data, comparisonData } = cohorts || {};

            const getAverage = (data) => {
              const values = data.filter(
                ({ revenueMetrics }) => revenueMetrics.ltv90 !== 0
              );
              if (!values.length) {
                return null;
              }

              return (
                values.reduce(
                  (sum, { revenueMetrics, nCAC }) =>
                    sum + (revenueMetrics.ltv90 || 0) * nCAC,
                  0
                ) / (values.length || 1)
              );
            };

            const value = data?.length > 0 ? getAverage(data) : 0;
            const secondaryValue =
              comparisonData?.length > 0 ? getAverage(comparisonData) : 0;

            if (value === null) {
              return {
                value: "No Data",
                secondaryValue: "Select an older date range",
              };
            }
            return {
              value,
              secondaryValue,
            };
          },
        },
        nCACPaybackPeriod: {
          title: "nCAC Payback Period",
          format: (value) =>
            value === "CAC Not Repaid"
              ? value
              : `${value} month${value !== 1 ? "s" : ""}`,
          accessor: (_, cohorts) => {
            const { data, comparisonData } = cohorts || {};

            const getAverage = (data) => {
              const repaidValues = data
                .filter(
                  ({ profitMetrics }) =>
                    profitMetrics.cacPayback !== "CAC Not Repaid"
                )
                .map(({ profitMetrics }) => {
                  return Number(profitMetrics.cacPayback);
                });

              if (repaidValues.length < data.length / 2) {
                return "CAC Not Repaid";
              }

              return Math.round(
                repaidValues.reduce((sum, item) => sum + item, 0) /
                  repaidValues.length
              );
            };

            const value = data?.length > 0 ? getAverage(data) : 0;
            const secondaryValue =
              comparisonData?.length > 0 ? getAverage(comparisonData) : 0;
            let diff;
            let diffClassName = "bg-muted";
            if (value === secondaryValue) {
              diff = "Same";
            } else if (
              value !== "CAC Not Repaid" &&
              secondaryValue !== "CAC Not Repaid"
            ) {
              diff = value - secondaryValue;
              diffClassName = diff > 0 ? "bg-chart-bad" : "bg-chart-good";
            }

            return {
              value,
              secondaryValue,
              diff,
              diffFormat: (value) =>
                value !== "Same"
                  ? `${value > 0 ? "+" : ""}${value} month${value !== 1 ? "s" : ""}`
                  : value,
              diffClassName,
            };
          },
        },
      },
    },
  ];
  return kpiCards;
};

// const chartConfig = {
//   upt: {
//     label: "UPT",
//     color: "var(--chart-1)",
//     yAxis: {
//       id: "right",
//       orientation: "right",
//       tickFormatter: numberFormat.whole,
//     },
//     type: "line",
//     valueFormatter: (value) => numberFormat.whole(value),
//   },
//   aur: { label: "AUR", color: "var(--chart-2)" },
//   profitContribution: { label: "Profit Contribution", color: "var(--chart-3)" },
// };

const KpiTitle = ({ title }) => {
  return (
    <DashboardLayout.SectionTitle className="col-span-3 mt-4 w-full">
      {title}
    </DashboardLayout.SectionTitle>
  );
};

const Dashboard = () => {
  const unitEconomics = useAnalytics(
    dashboardUnitEconomicsAtom,
    UNIT_ECONOMICS_QUERY,
    "unitEconomics"
  );
  const cohorts = useAnalytics(
    dashboardCohortAtom,
    COHORT_ANALYTICS_QUERY,
    "cohortAnalytics"
  );

  const { numberFormat } = unitEconomics;
  const kpiCards = useMemo(() => getKpiCards(numberFormat), [numberFormat]);

  const loading = unitEconomics.loading || cohorts.loading;
  const loaded = unitEconomics.store.loaded && cohorts.store.loaded;
  const showComparison =
    loaded &&
    ["previousPeriod", "previousYear"].includes(
      unitEconomics.store.comparisonMode
    );

  const onDateChange = (fn) => {
    unitEconomics.setStore(fn);
    cohorts.setStore(fn);
  };

  const onDateSubmit = () => {
    unitEconomics.refreshData();
    cohorts.refreshData();
  };

  // const chartData = useMemo(() => {
  //   if (!loaded) {
  //     return [];
  //   }
  //
  //   return unitEconomics.store.data.data
  //     .filter(
  //       (item) => item.period !== "Summary" && item.returningCustomer === null
  //     )
  //     .sort((a, b) => a.period.localeCompare(b.period))
  //     .map((item) => ({
  //       period: dateHeader(
  //         item.period,
  //         unitEconomics.store.resolution,
  //         unitEconomics.store.dateFrom,
  //         unitEconomics.store.dateTo
  //       ),
  //       upt: item.upt,
  //       aur: item.aur,
  //       profitContribution: item.profitContribution,
  //     }));
  // }, [loaded, unitEconomics.store]);

  return (
    <>
      <DashboardLayout.Header onRefresh={onDateSubmit} loading={loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters>
          <DateRangePicker
            atom={dashboardUnitEconomicsAtom}
            onSubmit={onDateSubmit}
            setStore={onDateChange}
            useComparison
            useReportingInterval
          />
        </DashboardLayout.Filters>

        <DashboardInsightsBand />

        {/*<DashboardLayout.Cards className="@2xl:grid-cols-[auto_.33fr]">*/}
        {/*  <DataChart*/}
        {/*    title="AOV Trend"*/}
        {/*    data={chartData}*/}
        {/*    xAxisDataKey="period"*/}
        {/*    chartConfig={chartConfig}*/}
        {/*    loading={loading}*/}
        {/*  />*/}

        {/*  <DashboardTutorialVideos />*/}
        {/*</DashboardLayout.Cards>*/}

        <DashboardLayout.Cards className="grid-cols-3">
          {kpiCards.map((section, index) => {
            return (
              <Fragment key={index}>
                {section.title && <KpiTitle title={section.title} />}
                <DashboardLayout.Cards
                  className={cn("col-span-3", section.className)}
                >
                  {Object.entries(section.cards).map(([key, card]) => {
                    const metric =
                      card?.accessor?.(
                        unitEconomics.store.data,
                        cohorts.store.data,
                        unitEconomics.store.resolution
                      ) || {};

                    const change = metric.secondaryValue
                      ? metric.value - metric.secondaryValue
                      : 0;
                    const diff =
                      metric.secondaryValue !== 0
                        ? change / Math.abs(metric.secondaryValue)
                        : 0;
                    const isBad = card.smallerIsGood ? diff > 0 : diff < 0;

                    return (
                      <KpiCard
                        key={key}
                        title={card.title}
                        format={card.format}
                        loading={loading}
                        value={loaded ? metric.value : null}
                        secondary={
                          showComparison ? metric.secondaryValue : null
                        }
                        secondaryFormat={card.format}
                        diff={
                          showComparison ? (metric.diff ?? diff * 100) : null
                        }
                        diffFormat={
                          metric.diffFormat || numberFormat.percentWithSign
                        }
                        diffClassName={cn(
                          isBad ? "bg-chart-bad" : "bg-chart-good",
                          metric.diffClassName
                        )}
                        chart={
                          metric.trend && {
                            data: metric.trend,
                            config: {
                              value: {
                                label: card.title,
                                color: !showComparison
                                  ? "var(--chart-1)"
                                  : isBad
                                    ? "var(--chart-bad)"
                                    : "var(--chart-good)",
                              },
                            },
                            dataKey: "period",
                          }
                        }
                        chartClassName={card.chartClassName}
                      />
                    );
                  })}
                </DashboardLayout.Cards>
              </Fragment>
            );
          })}
        </DashboardLayout.Cards>
      </DashboardLayout.Main>
    </>
  );
};

export default Dashboard;
